import React, { useContext, useEffect, useState } from "react";
import { Navbar } from "../../components/Navbar/Navbar";
import AuthContext from "../../context/AuthProvider";
import { toast } from "react-toastify";
import { DEFAULT_ERROR, GET_CHANNELS_URL, SEND_UNINVITE_URL, VERIFY_INVITE_URL } from "../../api/urls";
import useApi from "../../hooks/useApi";
import { Loading } from "../../components/loading";
import { FaSignOutAlt, FaUsers, FaExclamationTriangle } from "react-icons/fa";
import { AdditionalFormModal } from "./AdditionalFormModal";
import {
  ChannelsPage,
  PageHeader,
  JoinCard,
  JoinForm,
  JoinInput,
  JoinButton,
  SectionLabel,
  ChannelGrid,
  ChannelCard,
  ChannelCardInner,
  ChannelAvatar,
  ChannelName,
  ChannelLabel,
  LeaveButton,
  EmptyState,
  ModalBackdrop,
  ModalBox,
  ModalIcon,
  ModalTitle,
  ModalBody,
  ModalActions,
  ModalCancelBtn,
  ModalLeaveBtn,
} from "./style";

export const Channels = () => {
  const api = useApi();
  const { user, userUpdate } = useContext(AuthContext);
  const [channels, setChannels] = useState([]);
  const [isFetching, setIsFetching] = useState(true);
  const [joinCode, setJoinCode] = useState("");
  const [isJoining, setIsJoining] = useState(false);
  const [leavingId, setLeavingId] = useState(null);
  const [pendingLeave, setPendingLeave] = useState(null);
  const [pendingJoin, setPendingJoin] = useState(null);

  const fetchChannels = async () => {
    try {
      setIsFetching(true);
      const response = await api.get(GET_CHANNELS_URL);
      const data = response.data.channels || response.data || [];
      setChannels(Array.isArray(data) ? data : [data]);
    } catch {
      toast.error("Could not load your channels.");
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => { fetchChannels(); }, []);

  const completeJoin = async (payload) => {
    const response = await api.post(VERIFY_INVITE_URL, payload);
    toast.success(response.data.success || "You joined the channel!");
    const joined = response.data.channel || response.data.subscribed;
    const serverUser = response.data.user || {};
    const existing = Array.isArray(user.channels) ? user.channels : [];
    const merged = joined
      ? [...existing.filter((c) => (c.id || c._id) !== (joined.id || joined._id)), joined]
      : existing;
    userUpdate({ ...user, ...serverUser, channels: merged });
    await fetchChannels();
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    const code = joinCode.trim();
    if (!code) {
      toast.error("Please enter an invite code");
      return;
    }
    try {
      setIsJoining(true);
      await completeJoin({ inviteCode: code });
      setJoinCode("");
    } catch (error) {
      if (error?.response?.status === 422 && error.response.data?.required_fields) {
        setPendingJoin({ code, requiredFields: error.response.data.required_fields });
      } else {
        toast.error(error?.response?.data?.error || DEFAULT_ERROR);
      }
    } finally {
      setIsJoining(false);
    }
  };

  const handleAdditionalSubmit = async (extraFields) => {
    await completeJoin({ inviteCode: pendingJoin.code, additional_info: extraFields });
    setPendingJoin(null);
    setJoinCode("");
  };

  const confirmLeave = (channel) => setPendingLeave(channel);
  const cancelLeave = () => setPendingLeave(null);

  const handleLeave = async (channel) => {
    const channelId = channel.id || channel._id;
    setPendingLeave(null);
    try {
      setLeavingId(channelId);
      const resp = await api.post(SEND_UNINVITE_URL, { channelId });
      toast.success(resp.data.success || "You left the channel.");
      const serverUser = resp.data.user || {};
      const remaining = (user.channels || []).filter((c) => (c.id || c._id) !== channelId);
      userUpdate({ ...user, ...serverUser, channels: remaining });
      await fetchChannels();
    } catch (error) {
      toast.error(error?.response?.data?.error || DEFAULT_ERROR);
    } finally {
      setLeavingId(null);
    }
  };

  return (
    <>
      <Navbar />
      <ChannelsPage className="col-md-10 offset-md-1">
        <PageHeader>
          <h5>My Channels</h5>
          <p>Join a channel using an invite code, or leave channels you no longer want to follow.</p>
        </PageHeader>

        <JoinCard>
          <h6>Join a Channel</h6>
          <small>Paste the invite code shared by your tutor to join their channel.</small>
          <JoinForm onSubmit={handleJoin}>
            <JoinInput
              type="text"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value)}
              placeholder="Enter invite code"
            />
            <JoinButton type="submit" disabled={isJoining}>
              {isJoining ? "Joining…" : "Join Channel"}
            </JoinButton>
          </JoinForm>
        </JoinCard>

        <SectionLabel>
          Joined Channels ({isFetching ? "…" : channels.length})
        </SectionLabel>

        {isFetching ? (
          <Loading size={40} style={{ padding: "48px 0" }} />
        ) : channels.length === 0 ? (
          <EmptyState>
            <FaUsers size={48} />
            <h6>No channels yet</h6>
            <p>Enter an invite code above to join your first channel.</p>
          </EmptyState>
        ) : (
          <ChannelGrid>
            {channels.map((channel, i) => {
              const id = channel.id || channel._id || i;
              const name = [channel.firstname, channel.lastname].filter(Boolean).join(" ") || channel.name || "Channel";
              const initial = name[0] || "C";
              const isLeaving = leavingId === id;
              return (
                <ChannelCard key={id}>
                  <ChannelCardInner>
                    <ChannelAvatar>{initial}</ChannelAvatar>
                    <ChannelName>{name}'s Channel</ChannelName>
                    <ChannelLabel></ChannelLabel>
                    <LeaveButton onClick={() => confirmLeave(channel)} disabled={isLeaving}>
                      <FaSignOutAlt size={11} />
                      {isLeaving ? "Leaving…" : "Leave Channel"}
                    </LeaveButton>
                  </ChannelCardInner>
                </ChannelCard>
              );
            })}
          </ChannelGrid>
        )}
      </ChannelsPage>

      {pendingJoin && (
        <AdditionalFormModal
          requiredFields={pendingJoin.requiredFields}
          onSubmit={handleAdditionalSubmit}
          onClose={() => setPendingJoin(null)}
        />
      )}

      {pendingLeave && (() => {
        const name = [pendingLeave.firstname, pendingLeave.lastname].filter(Boolean).join(" ") || pendingLeave.name || "this channel";
        const isLeaving = leavingId === (pendingLeave.id || pendingLeave._id);
        return (
          <ModalBackdrop onClick={cancelLeave}>
            <ModalBox onClick={(e) => e.stopPropagation()}>
              <ModalIcon><FaExclamationTriangle /></ModalIcon>
              <ModalTitle>Leave Channel?</ModalTitle>
              <ModalBody>
                You are about to leave <strong>{name}'s Channel</strong>. You will lose access to their content and will need a new invite code to rejoin.
              </ModalBody>
              <ModalActions>
                <ModalCancelBtn onClick={cancelLeave}>Cancel</ModalCancelBtn>
                <ModalLeaveBtn onClick={() => handleLeave(pendingLeave)} disabled={isLeaving}>
                  {isLeaving ? "Leaving…" : "Leave Channel"}
                </ModalLeaveBtn>
              </ModalActions>
            </ModalBox>
          </ModalBackdrop>
        );
      })()}
    </>
  );
};
